import { useId } from 'react';
import { useSearchParams } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { usePropertyContext } from '@/context/PropertyContext';

interface CategoryFilterProps {
  className?: string;
}

/**
 * CategoryFilter - Accessible category filter tabs with icons
 * 
 * WCAG 2.1 AA Features:
 * - Role="tablist" with proper tab roles
 * - aria-selected state
 * - Keyboard navigation (arrow keys)
 * - Focus visible indicators
 * - Screen reader announcements
 */
export function CategoryFilter({ className }: CategoryFilterProps) {
  const id = useId();
  const { config } = usePropertyContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeCategory = searchParams.get('category');
  const categories = config?.categories || [];

  if (categories.length === 0) {
    return null;
  }

  const handleCategoryClick = (categorySlug: string | null) => {
    const params = new URLSearchParams(searchParams);
    if (categorySlug) {
      params.set('category', categorySlug);
    } else {
      params.delete('category');
    }
    // Reset to first page when filtering
    params.delete('offset');
    setSearchParams(params);
  };

  const handleKeyDown = (e: React.KeyboardEvent, currentIndex: number) => {
    const allItems = [null, ...categories.map(c => c.slug)];
    let newIndex = currentIndex;

    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      newIndex = currentIndex > 0 ? currentIndex - 1 : allItems.length - 1;
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      newIndex = currentIndex < allItems.length - 1 ? currentIndex + 1 : 0;
    } else if (e.key === 'Home') {
      e.preventDefault();
      newIndex = 0;
    } else if (e.key === 'End') {
      e.preventDefault();
      newIndex = allItems.length - 1;
    } else {
      return;
    }

    // Focus and activate the new tab
    const tabId = newIndex === 0 ? `${id}-all` : `${id}-${categories[newIndex - 1].slug}`;
    const tabElement = document.getElementById(tabId);
    if (tabElement) {
      tabElement.focus();
      handleCategoryClick(allItems[newIndex]);
    }
  };

  const allItems = [{ id: 'all', name: 'All', slug: null, icon: null }, ...categories];

  return (
    <div 
      className={cn('overflow-x-auto scrollbar-hide', className)}
      role="region"
      aria-label="Filter by category"
    >
      <div 
        role="tablist" 
        aria-label="Experience categories"
        className="flex gap-2 min-w-max"
      >
        {allItems.map((category, index) => {
          const isActive = category.slug === activeCategory || (category.slug === null && !activeCategory);
          const tabId = category.slug === null ? `${id}-all` : `${id}-${category.slug}`;
          
          return (
            <button
              key={category.id}
              id={tabId}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`${id}-panel`}
              tabIndex={isActive ? 0 : -1}
              onClick={() => handleCategoryClick(category.slug)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium',
                'whitespace-nowrap',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                'motion-safe:transition-all motion-safe:duration-200',
                isActive
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
              )}
            >
              {category.icon && (
                <span className="text-base" aria-hidden="true">
                  {category.icon}
                </span>
              )}
              {category.name}
            </button>
          );
        })}
      </div>
      
      {/* Hidden panel reference for accessibility */}
      <div id={`${id}-panel`} role="tabpanel" aria-label="Filtered experiences" className="sr-only">
        Currently showing: {activeCategory || 'All'} experiences
      </div>
    </div>
  );
}
